import { Checkbox, Skeleton } from '@arcadiapower/shrike';
import { useMemo, useState } from 'react';
import { Button } from '~/components/button';
import { ErrorMessage } from '~/components/error-message';
import { Form } from '~/components/form';
import { Container } from '~/components/layout';
import { ScrollableTermsDialog } from '~/components/terms/scrollable-terms';
import { TermsText } from '~/components/terms/terms-text';
import { Text } from '~/components/typography';
import { ContractVersion } from '~/typings/context';
import { ContractTag, ContractType } from '~/typings/graphql';

export type SolarTermsProps = {
  canSubmit: boolean; // use to track completion of other form elements outside of this component (e.g. lmi form)
  consolidateScrollableTermsText?: boolean;
  contracts: ContractVersion[];
  copy: { submitButton: string };
  error?: string;
  loading: boolean;
  pending: boolean;
  onSubmit: (payload: { contractIds: number[] }) => void;
  originationSourceType?: string;
  inReviewScreen?: boolean;
  showPersonalizedContractPreview?: boolean;
  utilityAccountId?: number;
};

export const SolarTerms = ({
  canSubmit,
  contracts,
  copy,
  error,
  loading,
  onSubmit,
  pending,
  originationSourceType,
  inReviewScreen,
  showPersonalizedContractPreview,
  utilityAccountId,
  consolidateScrollableTermsText,
}: SolarTermsProps) => {
  const [checked, setChecked] = useState(false);
  const [nysegChecked, setNysegChecked] = useState(false);

  const handleSubmit = () => {
    onSubmit({ contractIds: contracts.map(c => c.id) });
  };

  const contractCase = useMemo(() => {
    if (loading) return 'loading';
    if (contracts.length === 0) return 'empty';
    if (contracts.find(c => c.tag === ContractTag.Nyseg)) return 'nyseg';
    if (contracts.find(c => c.tag === ContractTag.MnLmi)) return 'mn-lmi';
    if (contracts.find(c => c.scrollRequired)) return 'scrollable';
    return 'standard';
  }, [contracts, loading]);

  return (
    <>
      {contractCase === 'loading' && (
        <Container>
          <Skeleton backgroundColor="tertiary" width="100%" />
          <Skeleton backgroundColor="tertiary" width="100%" />
          <Skeleton backgroundColor="tertiary" width="100%" />
        </Container>
      )}
      {contractCase === 'standard' && (
        <Form isValid={checked && canSubmit} onSubmit={handleSubmit}>
          <Checkbox checked={checked} onChange={e => setChecked(e.target.checked)}>
            <TermsText
              contracts={contracts}
              originationSourceType={originationSourceType}
              inReviewScreen={inReviewScreen}
              utilityAccountId={utilityAccountId}
              showPersonalizedContractPreview={showPersonalizedContractPreview}
            />
          </Checkbox>
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              type="submit"
              disabled={!(checked && canSubmit)}
              loading={pending}
            >
              {copy.submitButton}
            </Button>
          </Container>
        </Form>
      )}
      {contractCase === 'empty' && (
        <Form isValid={canSubmit} onSubmit={handleSubmit}>
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button mode="primary" type="submit" disabled={!canSubmit} loading={pending}>
              {copy.submitButton}
            </Button>
          </Container>
        </Form>
      )}
      {contractCase === 'nyseg' && (
        <Form isValid={checked && nysegChecked && canSubmit} onSubmit={handleSubmit}>
          <Checkbox
            margin="0 0 16px 0"
            checked={nysegChecked}
            onChange={e => setNysegChecked(e.target.checked)}
          >
            <Text textStyle="paragraph400" color="secondary">
              By checking this box, I understand and authorize Arcadia to enable electronic billing
              ("e-billing”) of my monthly electric utility billing statements.
            </Text>
          </Checkbox>
          <Checkbox checked={checked} onChange={e => setChecked(e.target.checked)}>
            <TermsText
              contracts={contracts.filter(c => c.tag !== ContractTag.Nyseg)}
              utilityAccountId={utilityAccountId}
              showPersonalizedContractPreview={showPersonalizedContractPreview}
            />
          </Checkbox>
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              type="submit"
              disabled={!(checked && nysegChecked && canSubmit)}
              loading={pending}
            >
              {copy.submitButton}
            </Button>
          </Container>
        </Form>
      )}
      {contractCase === 'mn-lmi' && (
        <ScrollableTermsDialog
          contracts={contracts.filter(c => c.contractType !== ContractType.Lmi)}
          onSubmit={handleSubmit}
          pending={pending}
          fetchPersonalizedContractForContractType={['disclosure', 'data_consent']}
          utilityAccountId={utilityAccountId!}
          showPersonalizedContractPreview
        >
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              disabled={!canSubmit}
              loading={pending}
              testId="view-scrollable-terms-button"
            >
              View terms
            </Button>
          </Container>
        </ScrollableTermsDialog>
      )}
      {contractCase === 'scrollable' && (
        <ScrollableTermsDialog
          contracts={contracts}
          onSubmit={handleSubmit}
          pending={pending}
          consolidateTermsText={consolidateScrollableTermsText}
        >
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              disabled={!canSubmit}
              loading={pending}
              testId="view-scrollable-terms-button"
            >
              View terms
            </Button>
          </Container>
        </ScrollableTermsDialog>
      )}
    </>
  );
};
